import React, { PureComponent } from 'react';

// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import styled from 'styled-components';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Collapse from '@material-ui/core/Collapse';
import Button from '../../../../../../SolvPatternLibrary/Button';
import SelectTextField from '../../../../../../SolvPatternLibrary/SelectTextField';
import { fontSize, fontFamily } from '../../../../../../../constants/text';
import { getAgeFromBirthDate } from '../../../../../../../core/util/date';
import { analyticsTrackEvent } from '../../../../../../../core/analytics';
import {
  ReminderStatus,
  DEFAULT_REMINDER_INTERVAL,
} from '../../../../../../Account/Profile/Reminders/util/reminders';
import {
  REMINDERS_ACTION,
  REMINDERS_SET_REMINDER,
} from '../../../../../../Account/Profile/Reminders/util/tracking';
import { getNumDaysFromTodayDate, isInFluSeason } from '../util';
import { TileIds } from '../../../util/tileEnums';
import { UserProfile } from '~/core/dapi/models';
import { Tile } from '../Tile';

const TitleText = styled.span.withConfig({ componentId: 'sc-1e33zst-0' })`
  font-size: ${fontSize.large};
  font-family: ${fontFamily.bold};
`;

const BodyText = styled(DialogContentText).withConfig({ componentId: 'sc-1e33zst-1' })`
  font-size: ${fontSize.default};
`;

const AlertText = styled(DialogContentText).withConfig({ componentId: 'sc-1e33zst-2' })`
  margin-top: 18px;
  font-size: ${fontSize.default};
`;

const SelectInput = styled(SelectTextField).withConfig({ componentId: 'sc-1e33zst-3' })`
  max-width: 300px;
  margin: 15px auto 0;

  & select {
    height: 52px;
  }

  && svg {
    display: inline-block;
  }
`;

const Actions = styled(DialogActions).withConfig({ componentId: 'sc-1e33zst-4' })`
  text-align: right;
`;

const ActionableButtons = styled(Button).withConfig({ componentId: 'sc-1e33zst-5' })`
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.mediumSmall};
  padding: 8px;
  font-weight: 300 !important;
  white-space: nowrap;
`;

export const ONE_WEEK_IN_DAYS = 7;
export const ONE_MONTH_IN_DAYS = 31;
export const THREE_MONTHS_IN_DAYS = 92;
export const SIX_MONTHS_IN_DAYS = 183;
export const NINE_MONTHS_IN_DAYS = 284;
export const TWELVE_MONTHS_IN_DAYS = 365;

const selectOptions = [
  {
    value: 1,
    label: '1 day',
  },
  {
    value: ONE_WEEK_IN_DAYS,
    label: '1 week',
  },
  {
    value: ONE_MONTH_IN_DAYS,
    label: '1 month',
  },
  {
    value: THREE_MONTHS_IN_DAYS,
    label: '3 months',
  },
  {
    value: SIX_MONTHS_IN_DAYS,
    label: '6 months',
  },
  {
    value: NINE_MONTHS_IN_DAYS,
    label: '9 months',
  },
  {
    value: TWELVE_MONTHS_IN_DAYS,
    label: '12 months',
  },
];

const REMINDER_DELAY_FIELD = 'reminder_delay';

type Props = {
  onClose: () => void;
  activeTile: Tile;
  createReminder: (
    userProfileId: string,
    birthDate: string,
    reminderType: string,
    firstReminderDate: string,
    notify: boolean,
    status: string
  ) => void;
  updateReminder?: () => void;
  setReminderDay?: () => void;
  form: formShape;
  userProfile: UserProfile;
  userReminders?: object;
};

class NextReminderDialog extends PureComponent<Props> {
  static MODAL_ID = 'next-reminder-modal';

  onSubmit = (wantReminder: any) => {
    const { form, activeTile, userProfile } = this.props;
    const age = getAgeFromBirthDate(userProfile.birth_date);
    form.validateFields((fieldErrors: any, values: any) =>
      this.setReminder(activeTile.reminder_type, age, this.reminderDate, wantReminder)
    );
  };

  onFieldChange = (value: any, fieldName: any) => {
    const { form } = this.props;
    form.setFieldsValue({ [fieldName]: value });
  };

  get reminderDate() {
    const { form } = this.props;
    const delay = Number(form.getFieldValue(REMINDER_DELAY_FIELD) || DEFAULT_REMINDER_INTERVAL);
    return getNumDaysFromTodayDate(delay);
  }

  get initialValue() {
    const {
      activeTile: { lastCheckupResponseInDays, reminder_type: reminderType },
    } = this.props;
    if (reminderType === TileIds.FLU_SHOT) return this.fluShotDefaultOption;
    if (!lastCheckupResponseInDays) return ONE_MONTH_IN_DAYS;

    const ideal = TWELVE_MONTHS_IN_DAYS - lastCheckupResponseInDays;

    // find closest match
    return selectOptions
      .map((o) => o.value)
      .reduce((prev, curr) => (Math.abs(curr - ideal) < Math.abs(prev - ideal) ? curr : prev));
  }

  get fluShotDefaultOption() {
    return selectOptions.find((option) => {
      const reminderDate = getNumDaysFromTodayDate(option.value);
      return isInFluSeason(reminderDate);
    })?.value;
  }

  setReminder = (reminderType: any, age: any, firstReminderDate: any, notify: any) => {
    const {
      createReminder,
      onClose,
      userProfile: { birth_date: birthDate, user_profile_id: userProfileId },
      activeTile: { doneResponse },
    } = this.props;

    const status = doneResponse ? ReminderStatus.DONE : ReminderStatus.NOT_DONE;
    createReminder(userProfileId, birthDate, reminderType, firstReminderDate, notify, status);

    analyticsTrackEvent(REMINDERS_ACTION, {
      reminderType,
      age,
      userProfileId,
      firstReminderDate,
      action: REMINDERS_SET_REMINDER,
      wantToBeReminded: notify,
    });

    onClose();
  };

  shouldDisplayFluShotAlert = () =>
    this.props.activeTile.reminder_type === TileIds.FLU_SHOT && !isInFluSeason(this.reminderDate);

  render = () => {
    const { activeTile, form } = this.props;

    return (
      <form>
        <DialogTitle id="next-reminder-form">
          <TitleText>
            {activeTile.doneResponse && ' Great! '}
            Would you like a reminder to book your next {activeTile.title}?
          </TitleText>
        </DialogTitle>

        <DialogContent>
          <BodyText>Yes! Send me a reminder in...</BodyText>

          <SelectInput
            fullWidth
            selectOptions={selectOptions}
            {...form.getFieldProps(REMINDER_DELAY_FIELD, {
              initialValue: this.initialValue,
            })}
            onChange={({ target: { value } }: any) =>
              this.onFieldChange(value, REMINDER_DELAY_FIELD)
            }
          />

          <Collapse in={this.shouldDisplayFluShotAlert()} timeout="auto">
            <AlertText>
              Typically flu shots are offered between September - February. You might want to change
              this reminder to fall in that date range.
            </AlertText>
          </Collapse>
        </DialogContent>

        <Actions>
          <ActionableButtons onClick={() => this.onSubmit(false)} variation="link">
            NO, THANKS
          </ActionableButtons>

          <ActionableButtons onClick={() => this.onSubmit(true)} variation="link">
            SAVE REMINDER
          </ActionableButtons>
        </Actions>
      </form>
    );
  };
}

export default createForm()(NextReminderDialog);
